import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { type Editor } from '@tiptap/react';
import { useIntl } from 'react-intl';
import { type SelectionActionItem } from './selection-ai-actions.config';
import { selectionAiActionsMessages } from './selection-ai-actions.messages';
import {
  ACTION_ROW_CLASS,
  type SelectionEditorActions,
} from './selection-ai-actions.utils';
import { SelectionMoreOptionsSubmenu } from './selection-more-options-submenu';
import { SelectionRewriteSubmenu } from './selection-rewrite-submenu';
import { useSelectionAiActionHandlers } from './use-selection-ai-action-handlers';
import { useSelectionAiMenuShownTracking } from './use-selection-ai-menu-shown-tracking';

export type { SelectionEditorActions } from './selection-ai-actions.utils';

export const SelectionAiActions = ({
  editor,
  onActionStart,
  getTargetRange,
  editorActions,
  showTopBorder = true,
  trackShownOnSelectionUpdate = true,
}: {
  editor: Editor;
  /**
   * Notified as soon as any action fires — lets a host menu (e.g. the section
   * kebab dropdown) close itself before the action's flow takes over.
   */
  onActionStart?: () => void;
  /**
   * Overrides the live selection as the action target (e.g. the section kebab
   * targets the whole section body without selecting it — a real selection
   * would also summon the text bubble menu).
   */
  getTargetRange?: () => { from: number; to: number } | null;
  /**
   * Editor actions to group under the "More options" submenu. Omit to hide
   * the row (e.g. the section kebab).
   */
  editorActions?: SelectionEditorActions;
  /**
   * Draws the top divider that separates the AI actions from the formatting
   * toolbar above (bubble menu). The section kebab supplies its own
   * `DropdownMenuSeparator`, so it turns this off to avoid a double divider.
   */
  showTopBorder?: boolean;
  /**
   * The bubble-menu host tracks from its final visibility predicate. Other
   * hosts, such as the section kebab, keep the default and track from the
   * target lifecycle.
   */
  trackShownOnSelectionUpdate?: boolean;
}) => {
  const intl = useIntl();
  const { visibleV2Actions } = useSelectionAiMenuShownTracking({
    editor,
    getTargetRange,
    subscribeToSelectionUpdate: trackShownOnSelectionUpdate,
  });
  const { handleActionClick, handleAskAnything } = useSelectionAiActionHandlers({
    editor,
    onActionStart,
    getTargetRange,
  });

  const renderActionRow = ({ id, icon: Icon, message }: SelectionActionItem) => (
    <button
      key={id}
      type="button"
      onClick={() => handleActionClick(id)}
      className={ACTION_ROW_CLASS}
      data-testid={`selection-ai-action-${id}`}
    >
      <Icon size={14} className="shrink-0 text-text-secondary" />
      {intl.formatMessage(message)}
    </button>
  );

  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 p-2',
        showTopBorder && 'border-t border-border'
      )}
      data-testid="selection-ai-actions"
    >
      <SelectionRewriteSubmenu onActionClick={handleActionClick} />
      {visibleV2Actions.map(renderActionRow)}
      {editorActions ? (
        <SelectionMoreOptionsSubmenu editorActions={editorActions} />
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAskAnything}
        className="mt-0.5 w-full"
        data-testid="selection-ai-ask-heidi-to-edit"
      >
        {intl.formatMessage(selectionAiActionsMessages.askHeidiToEdit)}
      </Button>
    </div>
  );
};
